/**
 * @fileoverview Painel lateral com o detalhe de uma OS recebida: triagem
 * (iniciar pelo botão e o que já houve), cliente, produto, nota fiscal,
 * solicitação e datas.
 */

import React, { useEffect, useState } from "react";
import { Drawer, IconButton, CircularProgress } from "@material-ui/core";
import { X } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import useStyles from "./styles";
import WorkOrderTriage from "./WorkOrderTriage";
import {
  EMPTY,
  formatAddress,
  formatDocument,
  formatLocalDateTime,
  formatOriginDate,
  formatPhone,
} from "./format";

const t = (key, options) => i18n.t(`workOrders.detail.${key}`, options);

const Field = ({ classes, label, children }) => (
  <div className={classes.field}>
    <span className={classes.fieldLabel}>{label}</span>
    <span className={classes.fieldValue}>{children || EMPTY}</span>
  </div>
);

const WorkOrderDetailDrawer = ({ workOrderId, onClose }) => {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!workOrderId) {
      setData(null);
      return undefined;
    }
    let active = true;
    setLoading(true);
    api
      .get(`/work-orders/${workOrderId}`)
      .then(({ data: body }) => active && setData(body))
      .catch((err) => {
        if (!active) return;
        toastError(err);
        onClose();
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [workOrderId, onClose]);

  const n = data?.normalized;
  const meta = n?.metadata || {};
  const visit = meta.visit?.start
    ? [formatOriginDate(meta.visit.start), meta.visit.end && formatOriginDate(meta.visit.end).slice(-5)]
        .filter(Boolean)
        .join(" – ")
    : null;

  return (
    <Drawer anchor="right" open={!!workOrderId} onClose={onClose} classes={{ paper: classes.drawerPaper }}>
      <div className={classes.drawerHeader}>
        <div>
          <div className={classes.drawerTitle}>{t("title", { number: data?.externalId || "" })}</div>
          {data && (
            <div className={classes.drawerSub}>
              {data.externalStatusLabel && <span className={classes.chip}>{data.externalStatusLabel}</span>}
              {data.woTypeLabel && (
                <span className={classes.chip}>
                  {[data.woTypeLabel, data.woSubType].filter(Boolean).join(" / ")}
                </span>
              )}
            </div>
          )}
        </div>
        <IconButton size="small" onClick={onClose} aria-label={t("close")}>
          <X size={18} />
        </IconButton>
      </div>

      <div className={classes.drawerBody}>
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress size={24} />
          </div>
        )}

        {!loading && data && !n && <div className={classes.purged}>{t("purged")}</div>}

        {!loading && n && (
          <>
            <WorkOrderTriage workOrderId={data.id} />

            <div className={classes.section}>
              <div className={classes.sectionTitle}>{t("customer")}</div>
              <Field classes={classes} label={t("name")}>{n.customer?.name}</Field>
              <Field classes={classes} label={t("document")}>
                {n.customer?.document && formatDocument(n.customer.document)}
              </Field>
              <Field classes={classes} label={t("phones")}>
                {(n.customer?.phones || []).map(formatPhone).join("\n")}
              </Field>
              <Field classes={classes} label={t("mentionedWhatsapp")}>
                {meta.mentionedWhatsapp && formatPhone(meta.mentionedWhatsapp)}
              </Field>
              <Field classes={classes} label={t("email")}>{n.customer?.email}</Field>
              <Field classes={classes} label={t("address")}>{formatAddress(n.customer?.address)}</Field>
            </div>

            <div className={classes.section}>
              <div className={classes.sectionTitle}>{t("product")}</div>
              <Field classes={classes} label={t("brandModel")}>
                {[n.product?.brand, n.product?.model].filter(Boolean).join(" ")}
              </Field>
              <Field classes={classes} label={t("serial")}>{n.product?.serialNumber}</Field>
              <Field classes={classes} label={t("category")}>
                {n.product?.category && n.product.category.replace(/-->/g, " › ")}
              </Field>
              <Field classes={classes} label={t("purchaseDate")}>
                {n.product?.purchaseDate && formatOriginDate(n.product.purchaseDate)}
              </Field>
              <Field classes={classes} label={t("warrantyStatus")}>
                {data.warrantyStatusLabel || meta.warrantyStatus}
              </Field>
              <Field classes={classes} label={t("warrantyEnd")}>
                {n.product?.warrantyEndDate && formatOriginDate(n.product.warrantyEndDate)}
              </Field>
            </div>

            <div className={classes.section}>
              <div className={classes.sectionTitle}>{t("invoice")}</div>
              <Field classes={classes} label={t("invoiceNumber")}>{n.invoice?.number}</Field>
              <Field classes={classes} label={t("reseller")}>{n.invoice?.reseller}</Field>
            </div>

            <div className={classes.section}>
              <div className={classes.sectionTitle}>{t("request")}</div>
              <div className={classes.longText}>{n.problemDescription || EMPTY}</div>
              <div style={{ marginTop: 10 }}>
                <Field classes={classes} label={t("symptom")}>{meta.symptom?.description}</Field>
                <Field classes={classes} label={t("notes")}>{meta.mideaNotes || meta.notes}</Field>
              </div>
            </div>

            <div className={classes.section}>
              <div className={classes.sectionTitle}>{t("service")}</div>
              <Field classes={classes} label={t("openedAt")}>{n.openedAt && formatOriginDate(n.openedAt)}</Field>
              <Field classes={classes} label={t("urgency")}>{data.urgencyLabel || meta.urgency}</Field>
              <Field classes={classes} label={t("visit")}>{visit}</Field>
              <Field classes={classes} label={t("acceptedAt")}>
                {meta.acceptedAt && formatOriginDate(meta.acceptedAt)}
              </Field>
            </div>
          </>
        )}

        {!loading && data && (
          <div className={classes.section}>
            <div className={classes.sectionTitle}>{t("receipt")}</div>
            <Field classes={classes} label={t("receivedAt")}>{formatLocalDateTime(data.receivedAt)}</Field>
            <Field classes={classes} label={t("lastChangedAt")}>{formatLocalDateTime(data.lastChangedAt)}</Field>
            <Field classes={classes} label={t("lastSeenAt")}>{formatLocalDateTime(data.lastSeenAt)}</Field>
            <Field classes={classes} label={t("source")}>{data.integration?.name}</Field>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default WorkOrderDetailDrawer;
